import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  Clock3,
  ExternalLink,
  Eye,
  FileText,
  Hourglass,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";

import api from "../../api/axios";
import { useAuth } from "../../context/AuthContext";

const getPolicyCacheKey = (employee) => `employee_policies_${employee?._id || employee?.id || "self"}`;

const readPolicyCache = (employee) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(getPolicyCacheKey(employee)) || "{}");
    return Array.isArray(parsed.data) ? parsed.data : [];
  } catch {
    return [];
  }
};

const writePolicyCache = (employee, policies) => {
  try {
    localStorage.setItem(getPolicyCacheKey(employee), JSON.stringify({ data: policies, savedAt: Date.now() }));
  } catch {
    // Live request can still fill the page.
  }
};

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const isImage = (policy) => String(policy?.document?.type || policy?.document?.data || "").startsWith("image/");
const isPdf = (policy) => String(policy?.document?.type || policy?.document?.data || "").includes("pdf");
const isHtml = (policy) => String(policy?.document?.type || policy?.document?.data || "").includes("html");

const compactDateTime = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatCard = ({ icon, label, value, subLabel, tone = "violet" }) => {
  const toneClass = {
    violet: "bg-violet-100 text-violet-700 dark:bg-violet-950/40 dark:text-violet-200",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200",
    red: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-200",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200",
  }[tone] || "bg-violet-100 text-violet-700";

  return (
    <div className="flex flex-col items-start gap-3 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm sm:flex-row sm:items-center sm:gap-5 sm:p-6">
      <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full sm:h-16 sm:w-16 ${toneClass}`}>
        {icon}
      </span>
      <div>
        <p className="text-sm font-black text-[var(--text-secondary)]">{label}</p>
        <strong className="mt-1 block text-2xl font-black text-[var(--text-primary)] sm:text-3xl">{value}</strong>
        <span className="text-sm font-medium text-[var(--text-secondary)]">{subLabel}</span>
      </div>
    </div>
  );
};

const PolicyIcon = ({ status }) => {
  const acknowledged = status === "Acknowledged";
  const overdue = status === "Overdue";
  const classes = acknowledged
    ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30"
    : overdue
      ? "bg-red-50 text-red-600 dark:bg-red-950/30"
      : "bg-amber-50 text-amber-600 dark:bg-amber-950/30";

  return (
    <span className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl sm:h-16 sm:w-16 ${classes}`}>
      <FileText size={26} />
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const icon = status === "Acknowledged" ? <CheckCircle2 size={16} /> : status === "Overdue" ? <AlertCircle size={16} /> : <Clock3 size={16} />;
  const classes = status === "Acknowledged"
    ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-200"
    : status === "Overdue"
      ? "bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-200"
      : "bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-200";

  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-black sm:px-5 sm:text-sm ${classes}`}>
      {icon}
      {status || "Pending"}
    </span>
  );
};

const PolicyPreview = ({ policy, onClose }) => {
  if (!policy) return null;
  return (
    <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
      <div className="m-auto flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-2xl">
        <div className="flex items-center justify-between gap-3 border-b border-[var(--border-color)] px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs font-black uppercase text-[var(--accent-primary)]">Policy preview</p>
            <h3 className="truncate text-sm font-black">{policy.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-xl hover:bg-[var(--bg-secondary)]">
            <X size={18} />
          </button>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-auto bg-[var(--bg-primary)] p-4">
          {isImage(policy) ? (
            <img src={policy.document.data} alt={policy.document.name || policy.title} className="max-h-full max-w-full rounded-xl object-contain" />
          ) : isPdf(policy) || isHtml(policy) ? (
            <iframe title={policy.title} src={policy.document.data} className="h-full min-h-[70vh] w-full rounded-xl border border-[var(--border-color)] bg-white" />
          ) : (
            <div className="max-w-md rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--card-bg)] p-8 text-center">
              <FileText size={30} className="mx-auto mb-3 text-[var(--accent-primary)]" />
              <p className="text-sm font-black">{policy.document?.name || policy.title}</p>
              <p className="mt-1 text-xs text-[var(--text-secondary)]">This file type may not preview in the browser.</p>
            </div>
          )}
        </div>
        <div className="flex justify-end border-t border-[var(--border-color)] px-4 py-3">
          <button type="button" onClick={onClose} className="rounded-xl bg-[var(--bg-secondary)] px-4 py-2 text-xs font-black">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const EmployeePolicies = () => {
  const { employee } = useAuth();
  const cachedPolicies = useMemo(() => readPolicyCache(employee), [employee]);
  const [policies, setPolicies] = useState(cachedPolicies);
  const [previewPolicy, setPreviewPolicy] = useState(null);
  const [updatingId, setUpdatingId] = useState("");
  const [indicator, setIndicator] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All Types");

  const fetchPolicies = useCallback(async () => {
    try {
      const res = await api.get("/api/employee/policies", { cache: false });
      const next = Array.isArray(res.data) ? res.data : [];
      setPolicies(next);
      writePolicyCache(employee, next);
    } catch (error) {
      console.error("Failed to load policies:", error);
    }
  }, [employee]);

  useEffect(() => {
    fetchPolicies();
    const id = window.setInterval(fetchPolicies, 2000);
    return () => window.clearInterval(id);
  }, [fetchPolicies]);

  const openPolicy = async (policy) => {
    setPreviewPolicy(policy);
    if (!policy.openedAt) {
      setPolicies((current) => current.map((item) => item._id === policy._id ? { ...item, openedAt: new Date().toISOString() } : item));
    }
    try {
      const res = await api.patch(`/api/employee/policies/${policy._id}/opened`, {}, { skipSaveToast: true });
      setPolicies((current) => {
        const next = current.map((item) => item._id === policy._id ? res.data : item);
        writePolicyCache(employee, next);
        return next;
      });
    } catch (error) {
      console.error("Failed to mark policy opened:", error);
    }
  };

  const acknowledgePolicy = async (policy) => {
    setUpdatingId(policy._id);
    setIndicator("Acknowledging policy...");
    const now = new Date().toISOString();
    const previous = policies;
    const optimistic = policies.map((item) =>
      item._id === policy._id
        ? { ...item, openedAt: item.openedAt || now, acknowledgedAt: now, acknowledgementStatus: "Acknowledged" }
        : item,
    );
    setPolicies(optimistic);
    writePolicyCache(employee, optimistic);

    try {
      const res = await api.patch(`/api/employee/policies/${policy._id}/acknowledge`, {}, { skipSaveToast: true });
      setPolicies((current) => {
        const next = current.map((item) => item._id === policy._id ? res.data : item);
        writePolicyCache(employee, next);
        return next;
      });
      setIndicator("Policy acknowledged.");
    } catch (error) {
      setPolicies(previous);
      writePolicyCache(employee, previous);
      setIndicator(error.response?.data?.message || "Could not acknowledge policy.");
    } finally {
      setUpdatingId("");
      window.setTimeout(() => setIndicator(""), 2200);
    }
  };

  const counts = policies.reduce((acc, policy) => {
    acc.total += 1;
    acc[policy.acknowledgementStatus] = (acc[policy.acknowledgementStatus] || 0) + 1;
    return acc;
  }, { total: 0, Pending: 0, Overdue: 0, Acknowledged: 0 });
  const categories = useMemo(
    () => ["All Types", ...new Set(policies.map((policy) => policy.category).filter(Boolean))],
    [policies],
  );
  const filteredPolicies = useMemo(() => {
    const query = search.trim().toLowerCase();
    return policies.filter((policy) => {
      const matchesSearch = !query || [policy.title, policy.category, policy.version, policy.document?.name]
        .some((value) => String(value || "").toLowerCase().includes(query));
      const matchesStatus = statusFilter === "All" || policy.acknowledgementStatus === statusFilter;
      const matchesType = typeFilter === "All Types" || policy.category === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [policies, search, statusFilter, typeFilter]);
  const recentActivity = useMemo(
    () => [...policies]
      .sort((a, b) => new Date(b.acknowledgedAt || b.openedAt || b.createdAt || 0) - new Date(a.acknowledgedAt || a.openedAt || a.createdAt || 0))
      .slice(0, 3),
    [policies],
  );
  const progress = counts.total ? Math.round((counts.Acknowledged / counts.total) * 100) : 0;

  return (
    <main className="employee-policies-page min-h-screen overflow-x-hidden bg-[var(--bg-primary)] text-[var(--text-primary)]">
      <div className="employee-policies-shell mx-auto space-y-5">
        <section className="relative overflow-hidden rounded-2xl border border-violet-100 bg-gradient-to-r from-white via-violet-50 to-violet-100/80 p-4 shadow-sm dark:border-violet-900/40 dark:from-[var(--card-bg)] dark:via-violet-950/20 dark:to-violet-950/30 sm:p-7">
          <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 shadow-sm dark:bg-violet-950/50 dark:text-violet-200 sm:h-16 sm:w-16">
                <ShieldCheck size={30} />
              </span>
              <div>
                <div className="mb-3 flex items-center gap-2 text-sm font-black uppercase tracking-wide text-[var(--accent-primary)]">
                  Policy Center
                </div>
                <h1 className="text-2xl font-black sm:text-3xl md:text-4xl">Policy acknowledgement</h1>
                <p className="mt-3 max-w-xl text-base font-medium text-[var(--text-secondary)]">
                  Review assigned policies and acknowledge them after reading.
                </p>
              </div>
            </div>

            <div className="hidden min-w-[360px] items-center justify-center lg:flex">
              <div className="relative h-36 w-80">
                <div className="absolute right-4 top-5 h-24 w-20 rotate-6 rounded-2xl bg-violet-500/20" />
                <div className="absolute right-24 top-0 h-32 w-24 -rotate-6 rounded-2xl bg-violet-600/25" />
                <div className="absolute right-12 top-0 flex h-36 w-28 rotate-3 flex-col gap-2 rounded-2xl border border-violet-200 bg-white p-4 shadow-xl dark:bg-slate-950">
                  <span className="mx-auto -mt-6 h-7 w-14 rounded-xl bg-violet-600" />
                  {[0, 1, 2].map((item) => (
                    <span key={item} className="flex items-center gap-2 rounded-lg bg-violet-50 px-2 py-1 dark:bg-violet-950/40">
                      <CheckCircle2 size={14} className="text-violet-600" />
                      <i className="h-2 flex-1 rounded-full bg-violet-200" />
                    </span>
                  ))}
                </div>
                <div className="absolute bottom-1 right-0 flex h-20 w-20 items-center justify-center rounded-3xl bg-violet-600 text-white shadow-xl">
                  <ShieldCheck size={36} />
                </div>
              </div>
            </div>
          </div>
          {indicator && (
            <div className="relative z-10 mt-5 inline-flex rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm font-black text-blue-700 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
              {indicator}
            </div>
          )}
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard icon={<FileText size={31} />} label="Total Policies" value={counts.total} subLabel="Assigned to you" />
          <StatCard icon={<Hourglass size={31} />} label="Pending" value={counts.Pending} subLabel="Awaiting your action" tone="amber" />
          <StatCard icon={<AlertCircle size={31} />} label="Overdue" value={counts.Overdue} subLabel="Past due date" tone="red" />
          <StatCard icon={<CheckCircle2 size={31} />} label="Acknowledged" value={counts.Acknowledged} subLabel="Completed" tone="emerald" />
        </section>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_430px]">
          <div className="min-w-0 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-3 shadow-sm sm:p-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
              <div className="relative flex-1">
                <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search policies..."
                  className="h-12 w-full rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] pl-12 pr-4 text-sm font-semibold outline-none transition focus:border-[var(--accent-primary)]"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
                {["All", "Pending", "Overdue", "Acknowledged"].map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setStatusFilter(status)}
                    className={`h-11 rounded-xl px-3 text-xs font-black transition sm:px-5 sm:text-sm ${statusFilter === status ? "bg-[var(--accent-primary)] text-white shadow-lg shadow-violet-500/20" : "bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--accent-primary)]/10"}`}
                  >
                    {status}
                  </button>
                ))}
              </div>
              <label className="relative w-full lg:w-[170px]">
                <select
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                  className="h-11 w-full appearance-none rounded-xl border border-transparent bg-[var(--bg-secondary)] px-4 pr-9 text-sm font-black outline-none"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
                <ChevronDown size={16} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
              </label>
            </div>

            <div className="mt-5 space-y-4">
              {!filteredPolicies.length ? (
                <div className="rounded-2xl border border-dashed border-[var(--border-color)] bg-[var(--bg-primary)] p-10 text-center">
                  <Clock3 className="mx-auto mb-3 text-[var(--text-secondary)]" size={30} />
                  <p className="font-bold text-[var(--text-secondary)]">{policies.length ? "No policies match your filters." : "No policies assigned yet."}</p>
                </div>
              ) : (
                filteredPolicies.map((policy) => (
                  <article key={policy._id} className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 sm:p-5">
                    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_1px_360px]">
                      <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:gap-5">
                        <PolicyIcon status={policy.acknowledgementStatus} />
                        <div className="min-w-0">
                          <h2 className="break-words text-lg font-black sm:text-xl">{policy.title}</h2>
                          <p className="mt-1 text-sm font-bold text-[var(--text-secondary)]">
                            {policy.category} <span className="mx-1">-</span> v{policy.version}
                            {policy.mandatory && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-xs font-black text-red-600 dark:bg-red-950/30 dark:text-red-200">Mandatory</span>}
                          </p>
                          <p className="mt-4 max-w-xl text-sm leading-6 text-[var(--text-secondary)]">
                            {policy.document?.name || `${policy.category || "Policy"} document for review and acknowledgement.`}
                          </p>
                          <div className="mt-6 grid gap-3 sm:flex sm:flex-wrap">
                            <button type="button" onClick={() => openPolicy(policy)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-[var(--accent-primary)] px-5 text-sm font-black text-[var(--accent-primary)]">
                              <Eye size={16} />
                              View Policy
                            </button>
                            <button type="button" onClick={() => openPolicy(policy)} className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-5 text-sm font-black text-white">
                              <ExternalLink size={16} />
                              Open Policy
                            </button>
                          </div>
                        </div>
                      </div>

                      <span className="hidden w-px bg-[var(--border-color)] lg:block" />

                      <div className="flex min-w-0 flex-col gap-4">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="space-y-4 text-sm">
                            <p className="flex gap-3"><CalendarClock size={18} className="mt-0.5 text-[var(--text-secondary)]" /><span><strong className="block text-[var(--text-secondary)]">Due Date</strong>{formatDate(policy.dueDate)}</span></p>
                            <p className="flex gap-3"><Clock3 size={18} className="mt-0.5 text-[var(--text-secondary)]" /><span><strong className="block text-[var(--text-secondary)]">Opened</strong>{formatDateTime(policy.openedAt)}</span></p>
                            <p className="flex gap-3"><CheckCircle2 size={18} className="mt-0.5 text-[var(--text-secondary)]" /><span><strong className="block text-[var(--text-secondary)]">Acknowledged</strong>{formatDateTime(policy.acknowledgedAt)}</span></p>
                          </div>
                          <StatusBadge status={policy.acknowledgementStatus} />
                        </div>
                        <button
                          type="button"
                          disabled={updatingId === policy._id || policy.acknowledgedAt}
                          onClick={() => acknowledgePolicy(policy)}
                          className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--accent-primary)] px-5 text-sm font-black text-white disabled:bg-emerald-100 disabled:text-emerald-700 dark:disabled:bg-emerald-950/30 dark:disabled:text-emerald-200 sm:w-auto"
                        >
                          <CheckCircle2 size={16} />
                          {policy.acknowledgedAt ? "Acknowledged" : "Acknowledge"}
                        </button>
                      </div>
                    </div>
                  </article>
                ))
              )}
            </div>
          </div>

          <aside className="min-w-0 space-y-4">
            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm sm:p-6">
              <h2 className="text-lg font-black">Compliance progress</h2>
              <div className="mt-6 flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6 xl:flex-col xl:items-start 2xl:flex-row 2xl:items-center">
                <div className="relative flex h-28 w-28 shrink-0 items-center justify-center rounded-full sm:h-32 sm:w-32" style={{ background: `conic-gradient(var(--accent-primary) ${progress * 3.6}deg, rgba(148, 163, 184, 0.22) 0deg)` }}>
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[var(--card-bg)] sm:h-24 sm:w-24">
                    <strong className="text-2xl font-black sm:text-3xl">{progress}%</strong>
                  </div>
                </div>
                <div>
                  <strong className="text-2xl font-black text-[var(--accent-primary)]">{counts.Acknowledged} of {counts.total}</strong>
                  <p className="mt-1 text-sm font-black">Policies completed</p>
                  <p className="mt-2 text-sm text-[var(--text-secondary)]">{counts.total === counts.Acknowledged ? "All assigned policies are complete." : "Keep going. Pending policies still need acknowledgement."}</p>
                </div>
              </div>
            </section>

            <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-sm sm:p-6">
              <h2 className="text-lg font-black">Recent activity</h2>
              <div className="mt-5 space-y-5">
                {recentActivity.length ? recentActivity.map((policy) => (
                  <div key={policy._id} className="flex gap-3">
                    <span className={`mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${policy.acknowledgementStatus === "Acknowledged" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {policy.acknowledgementStatus === "Acknowledged" ? <CheckCircle2 size={18} /> : <Clock3 size={18} />}
                    </span>
                    <div>
                      <p className="font-black">{policy.title}</p>
                      <p className="text-sm text-[var(--text-secondary)]">{policy.acknowledgementStatus}</p>
                      <p className="text-sm text-[var(--text-secondary)]">{compactDateTime(policy.acknowledgedAt || policy.openedAt || policy.createdAt)}</p>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm font-semibold text-[var(--text-secondary)]">No recent policy activity.</p>
                )}
              </div>
            </section>

          </aside>
        </section>
      </div>
      <PolicyPreview policy={previewPolicy} onClose={() => setPreviewPolicy(null)} />
    </main>
  );
};

export default EmployeePolicies;
